import { Fragment, useState, MouseEvent } from 'react';
import {
  Spinner, Anchor, BadgeNotifications, Avatar, Button,
} from '@components/common';
import { UserAccountLinksInterface } from '@type/header-types';
import { useBurgerMenuData } from '@components/layout/Header/MainNavbar/data/useBurgerMenuData';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { useAppSelector, useAppDispatch } from 'src/app';
import { useTranslation } from 'next-i18next';
import { signOut } from 'next-auth/react';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { getI18nLinkHref } from '@services/i18nPaths';
import { clearMyJobsDefault } from '@services/my-jobs/defaultPage';
import { LanguageList } from '@components/layout/LanguageSwitch/LanguageList';
import { NeuroIcon } from '@components/common/Icons';


/**
 * @description Renders the links in the burger menu
 * @param props
 */
export const BurgerMenuLinks = (props: UserAccountLinksInterface) => {
  const { locale } = useExtendedRouter();
  // Component props with defaults
  const {
    className = '',
    closePopoverDesktop = () => {},
  } = props;

  // Helpers
  const { t } = useTranslation('common');
  const appDispatch = useAppDispatch();


  // Get the burger menu data with SWR
  // *****************************************
  const { burgerMenuData, burgerMenuLoading } = useBurgerMenuData();


  // Get the headerInfo data from redux
  // *****************************************
  const headerInfo = useAppSelector((state) => state.user.headerInfo);


  // Base anchor classes. Add the color classes to mark special links
  // *****************************************
  const baseClasses = 'cursor-pointer flex space-between gap-4 items-center py-2.5 px-3.5 text-base my-1 lg:gap-3 lg:py-1.5 lg:transition-all lg:duration-200 lg:ease-out';
  const defaultColor = 'hover:text-primary hover:bg-surface';

  const totalProfileItems = burgerMenuData?.filter((menu) => menu.type !== undefined).length;
  const [profileItemsLimit, setProfileItemsLimit] = useState(5);

  // Messages lives in the Salaries section (moved out of the desktop navbar icon); the
  // unread badge here is the only count indicator now.
  const messagesItem = burgerMenuData?.find((menu) => menu.type === undefined && menu.id === 'messages');


  // Handle logout with NextAuth
  // *****************************************
  const handleLogout = (e: MouseEvent<HTMLButtonElement>) => {
    closePopoverDesktop(e);

    // Clear Redux state immediately
    appDispatch({ type: 'user/clearHeaderInfo' });

    // The My Jobs default is a 7-day, path=/ cookie — drop it so the next login on this
    // browser is routed by its own account, not by the person who just signed out.
    clearMyJobsDefault();

    // Sign out from NextAuth and redirect to home page to reset all state and avoid rendering protected pages
    void signOut({ redirect: false }).then(() => {
      window.location.href = locale === 'ro' ? '/' : `/${locale}`;
    });
  };


  // Render component
  // *****************************************
  return (
    <div className={`flex flex-col ${className}`}>
      { burgerMenuLoading && <div className="flex items-center justify-center py-10"><Spinner /></div> }

      { // Render links. Use Anchor because there's a mix with symfony links
        (!burgerMenuLoading && burgerMenuData) && (
          <>
            {burgerMenuData.filter((menu) => menu.type !== undefined).slice(0, profileItemsLimit).map((item) => (
              <Fragment key={item.id}>
                {item && item?.id && (
                <Anchor
                  styling="none"
                  href={item.link}
                  onClick={closePopoverDesktop}
                  className={`${baseClasses} ${defaultColor} tr-${item.tracking} flex justify-between`}
                >
                  <span className="truncate">
                    {item.label}
                    {item.type === 'personal_profile'
                      && (
                      <span className="text-pico">
                        {' '}
                        (
                        {t('global.label.cv.edit')}
                        )
                      </span>
                      )}
                  </span>

                  {item.icon && (
                    <Avatar
                      size="xs"
                      imgPath={item.icon}
                      imgFolder={item.type === 'employer_profile' ? 'employer_logo' : 'resume_image'}
                      aspectRatio="square-uncertain"
                      extractImageName
                      withRing
                    />
                  )}
                </Anchor>
                )}
              </Fragment>
            ))}
            {Number(totalProfileItems) > profileItemsLimit && (
              <Button
                styling="none"
                onClick={() => setProfileItemsLimit(burgerMenuData.filter((menu) => menu.type !== undefined).length)}
                className={`${baseClasses} ${defaultColor} flex justify-between`}
              >
                {t('global.label.see_all')}
                <ChevronDownIcon
                  className="w-7 rounded-full bg-white"
                />
              </Button>
            )}

            { /* Messages + Salaries — own section, divider above and below */ }
            <hr className="my-4" />
            {messagesItem && (
              <Anchor
                styling="none"
                href={messagesItem.link}
                onClick={closePopoverDesktop}
                className={`${baseClasses} ${defaultColor} tr-${messagesItem.tracking}`}
              >
                {messagesItem.label}
                <BadgeNotifications count={headerInfo?.nrUnseenMessages || 0} />
              </Anchor>
            )}
            <Anchor
              styling="none"
              href={getI18nLinkHref('/salaries', locale)}
              onClick={closePopoverDesktop}
              className={`${baseClasses} ${defaultColor} tr-salaries`}
            >
              {t('global.label.salaries')}
            </Anchor>
            { /* Wallet — moved here from the navbar icon; balance + neuro symbol on the right */ }
            <Anchor
              styling="none"
              href={getI18nLinkHref('/wallet/transactions', locale)}
              onClick={closePopoverDesktop}
              className={`${baseClasses} ${defaultColor} justify-between tr-wallet`}
            >
              {t('header.label.wallet')}
              <span className="flex items-center gap-0.5 font-bold">
                {headerInfo?.walletCredits ?? 0}
                <NeuroIcon className="size-2" />
              </span>
            </Anchor>

            { /* Subscriptions — shown only to an impersonating admin, or to an account
                 that already holds a subscription. The feature is deliberately
                 invisible to everyone else during the soft launch; the route itself
                 is guarded server-side, this only controls discoverability. */ }
            {(headerInfo?.adminUser || headerInfo?.hasAccountSubscription) && (
              <Anchor
                styling="none"
                href={getI18nLinkHref('/subscriptions', locale)}
                onClick={closePopoverDesktop}
                className={`${baseClasses} ${defaultColor} tr-subscriptions`}
              >
                {t('subscription.page.title')}
              </Anchor>
            )}

            <hr className="my-4" />
            {burgerMenuData.filter((menu) => menu.type === undefined && menu.id !== 'messages').map((item) => (
              <Fragment key={item.id}>
                {item && item?.id && item?.id !== 'logout' && (
                  <Anchor
                    styling="none"
                    href={item.link}
                    onClick={closePopoverDesktop}
                    className={`${baseClasses} ${defaultColor} tr-${item.tracking}`}
                  >
                    {item.label}
                  </Anchor>
                )}
                {/* Site language switcher — for authenticated users it lives here, right after account settings */}
                {item?.id === 'settings' && (
                  <div className="my-1">
                    <LanguageList withCurrentLocaleName />
                  </div>
                )}
                {item && item?.id && item?.id === 'logout' && (
                  <Button
                    styling="none"
                    onClick={handleLogout}
                    className={`${baseClasses} ${defaultColor} tr-${item.tracking}`}
                  >
                    {item.label}
                  </Button>
                )}
              </Fragment>
            ))}
          </>
        )
}
    </div>
  );
};
